import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { actions as authActions } from 'features/Auth';
import {
  checkPaymentsAllowed,
  MAX_PAYMENT_AMOUNT,
  PAYMENT_RESULT,
  PAYMENT_STEPS,
  PAYMENT_TIMER_SECONDS,
  PENDING_TIMER_SECONDS,
  resolveTimerSeconds,
} from 'features/finance/data';
import { actions as financeActions } from 'features/finance';

const usePayments = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const authData = useSelector(state => state.auth);
  const locale = useSelector(state => state.locale.locale);

  const isAllowed = checkPaymentsAllowed(authData);
  const maxDeposit = authData.maxDeposit ?? MAX_PAYMENT_AMOUNT;

  const [step, setStep] = useState(PAYMENT_STEPS.AMOUNT);
  const [amount, setAmount] = useState('');
  const [preorder, setPreorder] = useState(null);
  const [resultStatus, setResultStatus] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [timerSeconds, setTimerSeconds] = useState(PAYMENT_TIMER_SECONDS);
  const [isProcessing, setIsProcessing] = useState(false);

  const waitingStartedAtRef = useRef(null);
  const isFinishedRef = useRef(false);
  const isProcessingRef = useRef(false);

  useEffect(() => {
    if (!authData.isAuth) return;
    if (!isAllowed) history.replace('/');
  }, [authData.isAuth, history, isAllowed]);

  useEffect(() => {
    if (!isAllowed) return;
    dispatch(authActions.getMetadata());
  }, [dispatch, isAllowed]);

  const paymentAmount = preorder?.preorderAmount ?? amount;

  const handlePaymentResult = status => {
    if (isFinishedRef.current) return;
    isFinishedRef.current = true;

    const startedAt = waitingStartedAtRef.current;
    setElapsedSeconds(startedAt ? Math.floor((Date.now() - startedAt) / 1000) : 0);
    setResultStatus(status);
    setStep(PAYMENT_STEPS.RESULT);
    isProcessingRef.current = false;
    setIsProcessing(false);
  };

  const handleAmountSubmit = async () => {
    if (isProcessingRef.current) return;

    isProcessingRef.current = true;
    setIsProcessing(true);
    const response = await dispatch(financeActions.createRevshareDepositPreorder({ amount }));

    if (!response?.success) {
      isProcessingRef.current = false;
      setIsProcessing(false);
      return;
    }

    isProcessingRef.current = false;
    setIsProcessing(false);
    setPreorder(response.data);
    setTimerSeconds(resolveTimerSeconds(response.data.expiresAt, PAYMENT_TIMER_SECONDS));
    setStep(PAYMENT_STEPS.PURCHASE);
  };

  const handlePurchaseSubmit = async () => {
    if (isProcessingRef.current || !preorder?.id || isFinishedRef.current) return;

    isProcessingRef.current = true;
    waitingStartedAtRef.current = Date.now();
    setTimerSeconds(resolveTimerSeconds(preorder.expiresAt, PENDING_TIMER_SECONDS));
    setStep(PAYMENT_STEPS.WAITING);

    const response = await dispatch(
      financeActions.confirmRevshareDeposit({
        requestId: preorder.id,
        amount: preorder.preorderAmount ?? amount,
      }),
    );

    if (response?.success) {
      dispatch(authActions.getBalance());
      handlePaymentResult(PAYMENT_RESULT.SUCCESS);
      return;
    }

    handlePaymentResult(PAYMENT_RESULT.ERROR);
  };

  const handleExpire = () => {
    handlePaymentResult(PAYMENT_RESULT.ERROR);
  };

  const handleRestart = () => {
    isFinishedRef.current = false;
    isProcessingRef.current = false;
    waitingStartedAtRef.current = null;
    setAmount('');
    setPreorder(null);
    setResultStatus(null);
    setElapsedSeconds(0);
    setTimerSeconds(PAYMENT_TIMER_SECONDS);
    setIsProcessing(false);
    setStep(PAYMENT_STEPS.AMOUNT);
  };

  return {
    isAllowed: authData.isAuth && isAllowed,
    isProcessing,
    locale,
    step,
    amount,
    setAmount,
    maxDeposit,
    preorder,
    paymentAmount,
    resultStatus,
    elapsedSeconds,
    timerSeconds,
    handleAmountSubmit,
    handlePurchaseSubmit,
    handleExpire,
    handleRestart,
  };
};

export default usePayments;
